const integerFormatter = new Intl.NumberFormat('en-US', {
    useGrouping: true,
    maximumFractionDigits: 0
});

/**
 * Format Number
 *
 * "0" => "0.00"
 * "-0" => "-0.00"
 * "117243" => "117,243.00"
 * "115372.23414" => "115,372.23414"
 * "1615220.9" => "1,615,220.9"
 * "101.84360000000001" => "101.8436" (with maxFraction: 8)
 * "103.12064000" => "103.12064" (trim zeros after maxFraction)
 * "103.00000000" => "103.00" (keep .00 for integers)
 * "0.07207360" => "0.0720736" (trim trailing zeros)
 * "abc1234.56789" => "1,234.57" (strip letters, adaptive rounding)
 * "0.0000001234" => "0.0000001234" (preserve ultra-small, no zeroing)
 * "0.0056789123" => "0.005679" (adaptive rounding)
 * "0.12345678" => "0.12346" (adaptive rounding)
 * "12.987654" => "12.9877" (adaptive rounding)
 * "1234.56789" => "1,234.57" (adaptive rounding)
 * "1234567.89123" => "1,234,567.89" (adaptive rounding)
 */
export function formatNumber(value, { maxFraction, abs = false } = {}) {
    if (value === null || value === undefined || value === '') return '';

    if (typeof value === 'number' && !Number.isFinite(value)) return '';

    const isNegativeZero = typeof value === 'number' && Object.is(value, -0);

    let raw = `${value}`.trim();
    if (!raw) return '';

    raw = raw.replace(/\p{L}/gu, '').trim();
    if (!raw) return '';

    let sign = '';
    if (raw[0] === '-') {
        sign = abs ? '' : '-';
        raw = raw.slice(1);
    } else if (raw[0] === '+') {
        raw = raw.slice(1);
    }

    if (isNegativeZero && !abs) {
        sign = '-';
    }

    raw = raw.replace(/,/g, '');
    if (!/^\d*\.?\d*$/.test(raw)) return '';

    let [intPart, ...decParts] = raw.split('.');
    let decPart = decParts.length ? decParts.join('') : '';

    intPart = intPart.replace(/\D/g, '');
    decPart = decPart.replace(/\D/g, '');

    const shouldUseAdaptive = typeof maxFraction !== 'number';
    const adaptiveConfig = shouldUseAdaptive
        ? getAdaptiveFormatConfig(intPart, decPart)
        : null;
    const resolvedMaxFraction = shouldUseAdaptive
        ? adaptiveConfig.maxFraction
        : maxFraction;

    if (typeof resolvedMaxFraction === 'number' && resolvedMaxFraction >= 0) {
        if (shouldUseAdaptive && adaptiveConfig.rounding === 'round') {
            const rounded = roundDecimalParts(
                intPart,
                decPart,
                resolvedMaxFraction
            );
            intPart = rounded.intPart;
            decPart = rounded.decPart;
        } else {
            const sliced = decPart.slice(0, resolvedMaxFraction);
            if (shouldUseAdaptive && adaptiveConfig.rounding === 'truncate') {
                const slicedHasValue = sliced.replace(/0/g, '');
                const originalHasValue = decPart.replace(/0/g, '');
                decPart = slicedHasValue || !originalHasValue ? sliced : decPart;
            } else {
                decPart = sliced;
            }
        }

        decPart = decPart.replace(/0+$/, '');
    }

    if (!intPart && !decPart) return '';

    if (!intPart) intPart = '0';

    let formattedInt = '';
    try {
        formattedInt = integerFormatter.format(BigInt(intPart));
    } catch (error) {
        formattedInt = integerFormatter.format(Number(intPart));
    }

    let formatted = formattedInt;
    if (decPart.length) {
        formatted += `.${decPart}`;
    } else {
        formatted += '.00';
    }

    return sign ? `${sign}${formatted}` : formatted;
}

function normalizeIntString(value) {
    const normalized = value.replace(/^0+/, '');
    return normalized ? normalized : '0';
}

function compareIntStrings(a, b) {
    const left = normalizeIntString(a);
    const right = normalizeIntString(b);
    if (left.length !== right.length) {
        return left.length > right.length ? 1 : -1;
    }
    if (left === right) return 0;
    return left > right ? 1 : -1;
}

function incrementIntString(value) {
    const digits = value.split('');
    let carry = 1;

    for (let i = digits.length - 1; i >= 0; i -= 1) {
        const sum = (digits[i].charCodeAt(0) - 48) + carry;
        if (sum === 10) {
            digits[i] = '0';
            carry = 1;
        } else {
            digits[i] = String(sum);
            carry = 0;
            break;
        }
    }

    if (carry) {
        digits.unshift('1');
    }

    return digits.join('');
}

function roundDecimalParts(intPart, decPart, maxFraction) {
    if (maxFraction <= 0) {
        if (decPart[0] && decPart[0] >= '5') {
            return { intPart: incrementIntString(intPart), decPart: '' };
        }
        return { intPart, decPart: '' };
    }

    if (decPart.length <= maxFraction) {
        return { intPart, decPart };
    }

    const roundDigit = decPart[maxFraction];
    let kept = decPart.slice(0, maxFraction);

    if (roundDigit >= '5') {
        const digits = kept.split('');
        let carry = 1;

        for (let i = digits.length - 1; i >= 0; i -= 1) {
            const sum = (digits[i].charCodeAt(0) - 48) + carry;
            if (sum === 10) {
                digits[i] = '0';
                carry = 1;
            } else {
                digits[i] = String(sum);
                carry = 0;
                break;
            }
        }

        if (carry) {
            intPart = incrementIntString(intPart);
            kept = '0'.repeat(maxFraction);
        } else {
            kept = digits.join('');
        }
    }

    return { intPart, decPart: kept };
}

function getAdaptiveFormatConfig(intPart, decPart) {
    const normalizedInt = normalizeIntString(intPart);
    const normalizedDec = decPart.replace(/0+$/, '');
    const firstNonZero = normalizedDec.search(/[1-9]/);

    if (normalizedInt !== '0') {
        const cmp = compareIntStrings(normalizedInt, '100');
        if (cmp > 0 || (cmp === 0 && normalizedDec)) {
            return { maxFraction: 2, rounding: 'round' };
        }
        return { maxFraction: 4, rounding: 'round' };
    }

    if (firstNonZero === -1) {
        return { maxFraction: 2, rounding: 'round' };
    }

    if (firstNonZero >= 4) {
        return { maxFraction: 8, rounding: 'truncate' };
    }

    if (firstNonZero >= 2) {
        return { maxFraction: 6, rounding: 'round' };
    }

    return { maxFraction: 5, rounding: 'round' };
}

/**
 * Format Number With Sign
 *
 * "12" => "+12.00"
 * "-12" => "-12.00"
 * "-0" => "-0.00"
 * "0.07207360" => "+0.0720736" (with maxFraction: 8)
 */
export function formatNumberWithSign(
    value,
    { showPlus = true, maxFraction } = {}
) {
    if (value === null || value === undefined || value === '') return '';

    const isNegativeZero = typeof value === 'number' && Object.is(value, -0);
    let raw = `${value}`.trim();
    if (!raw) return '';

    let sign = '';
    if (raw[0] === '-') {
        sign = '-';
        raw = raw.slice(1);
    } else if (raw[0] === '+') {
        sign = showPlus ? '+' : '';
        raw = raw.slice(1);
    }

    if (isNegativeZero) {
        sign = '-';
    }

    const formatted = formatNumber(raw, { maxFraction });
    if (!formatted) return '';

    if (!sign && showPlus) {
        const numericValue = Number(value);
        if (!Number.isNaN(numericValue) && numericValue > 0) {
            sign = '+';
        }
    }

    return sign ? `${sign}${formatted}` : formatted;
}

/**
 * Format Currency With Sign
 *
 * "12", "$" => "+$12.00"
 * "-12", "$" => "-$12.00"
 * "-0", "$" => "-$0.00"
 * "101.84360000000001", "$" => "+$101.8436" (with maxFraction: 8)
 * "103.12064000", "$" => "+$103.12064" (trim zeros after maxFraction)
 * "103.00000000", "$" => "+$103.00" (keep .00 for integers)
 */
export function formatCurrencyWithSign(
    value,
    currencySymbol,
    { showPlus = true, maxFraction } = {}
) {
    const formatted = formatNumberWithSign(value, {
        showPlus,
        maxFraction
    });

    if (!formatted) return '';
    if (!currencySymbol) return formatted;

    const firstChar = formatted[0];
    if (firstChar === '+' || firstChar === '-') {
        return `${firstChar}${currencySymbol}${formatted.slice(1)}`;
    }

    return `${currencySymbol}${formatted}`;
}
